import {sensitiveDataCleanerMiddleware} from './middlewares/sensitiveDataCleanerMiddleware'
import {getEnv} from './shared/env'

type Log = {
  component: string
  level: 'warn' | 'error' | 'debug' | 'info' | 'fatal'
  event: string
  data: unknown
}

type EventLog = {
  component: string
  event: string
  data?: Record<string, unknown>
}

type EnvInfo = {
  /** Основная версия приложения */
  appVersion?: string
  /** ID устройства */
  deviceId?: string
  /** Флаг авторизованности */
  isLoggedIn?: boolean
  /** Нативная версия приложения */
  nativeVersion?: string
  partnerID?: string
  userEmail?: string
  countryCode?: string
  os?: 'native-ios' | 'native-android' | 'web-ios' | 'web-android' | 'web'
  platform?: 'desktop' | 'pad' | 'touch'
  application?:
    | 'restapp_desktop_web'
    | 'restapp_tablet_web_ios'
    | 'restapp_tablet_web_android'
    | 'restapp_mobile_web_ios'
    | 'restapp_mobile_web_android'
  userAgent?: string
  sessionId?: string
}

type QueueEvents = Log & {
  env?: EnvInfo
  timestamp: string
  route: string
}

/** @deprecated используй errorLogger или eventLogger */
export type Logger = ReturnType<typeof createLogger>

const DEFAULT_LOG_INTERVAL = 1500

let envInfo: EnvInfo = {
  userAgent: window.navigator.userAgent
}
let QUEUE_EVENTS: QueueEvents[] = []

/** @deprecated используй errorLogger или eventLogger */
export function setLoggerEnvInfo(data: EnvInfo) {
  return (envInfo = Object.assign(envInfo, data))
}
/** @deprecated используй errorLogger или eventLogger */
export function getLoggerEnvInfo() {
  return envInfo
}

interface LoggerOptions {
  component: string
  eventsTransport?: (event: EventLog) => void
  // TODO: Вынести отправку в кибану в отдельный транспорт.
  // TODO: Транспорт и метод для отправки в ErrorBooster
}

/**
 * @param component - общая область для агрегации логов, для заказов могут быть `orders` или что-то еще общее по отрпавляемым логам
 * @param eventsTransport - транспорт использующийся для событий logger.event, если не передан при создании логера ивента никуда отсылаться не будут
 * @description - методы warn, error, debug, info, fatal - самостоятельно отправляют логи в кибану. event - отправляет ивенты в транспорт переданный при создании логера
 *
 * @examples
 *
```javascript
import {createLogger} from '@eda-restapp/logger'

const logger = createLogger({component: 'orders'})
logger.info('new', '230523-5679325')
logger.info('update', '230523-5679325')
logger.info('change', {
    orderId: '230523-5679325',
    newStatus: 'accepted',
    oldStatus: 'released'
})
```
@deprecated используй errorLogger или eventLogger
 */
export function createLogger({component, eventsTransport}: LoggerOptions) {
  return {
    warn: (event: Log['event'], data?: Log['data']) =>
      sendLogBeacon({
        level: 'warn',
        component,
        event,
        data
      }),
    error: (event: Log['event'], data?: Log['data']) =>
      sendLogBeacon({
        level: 'error',
        component,
        event,
        data
      }),
    debug: (event: Log['event'], data?: Log['data']) =>
      sendLogBeacon({
        level: 'debug',
        component,
        event,
        data
      }),
    info: (event: Log['event'], data?: Log['data']) =>
      sendLogBeacon({
        level: 'info',
        component,
        event,
        data
      }),
    fatal: (event: Log['event'], data?: Log['data']) =>
      sendLogBeacon({
        level: 'fatal',
        component,
        event,
        data
      }),
    event: (event: string, data?: Record<string, unknown>): void => {
      if (!eventsTransport) {
        console.log('Provide events transport for sending events')
        return
      }
      eventsTransport(sensitiveDataCleanerMiddleware({event, component, data}))
    }
  }
}

/** @deprecated используй errorLogger или eventLogger */
export const sendLogBeacon = (event: Log) => {
  if (getEnv().LOGGER_DISABLE_VENDOREVENT) {
    return
  }

  QUEUE_EVENTS.push(
    sensitiveDataCleanerMiddleware<QueueEvents>(
      Object.assign(event, {
        timestamp: new Date().toISOString(),
        data: event.data ? JSON.stringify(event.data) : `no_data`,
        route: window.location.pathname
      })
    )
  )
}

setInterval(async () => {
  if (QUEUE_EVENTS.length) {
    const env = getLoggerEnvInfo()
    fetch('/web-api/log/vendorevent', {
      method: 'POST',
      mode: 'same-origin',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        logs: QUEUE_EVENTS.map((event) =>
          // Стараемся передать наиболее полный env
          Object.assign(event, {
            env: env
          })
        )
      })
    })
    QUEUE_EVENTS = []
  }
}, DEFAULT_LOG_INTERVAL)
